import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import {
  INVENTORY_ENDPOINTS,
  INVENTORY_GET_PRODUCT_ITEMS_ENDPOINT,
} from '../api/api-endpoints';
import { RestApiService } from '../api/rest-api.service';
import { TokenService } from './token.service';
import { environment } from '../../../environments/environment';
import { 
  CreateInventoryProductVariantPayload, 
  CreateInventoryTransferPayload, 
  InventoryBulkImportProductRowPayload, 
  InventoryBulkImportResponse, 
  InventoryBulkImportRowResult, 
  InventoryProduct,
  InventoryProductItem,
  InventoryProductAttributeLine, 
  InventoryProductAttributeOption, 
  InventoryProductAttributeValueOption, 
  InventoryProductBulkAction, 
  InventoryProductBulkActionItemResult, 
  InventoryProductBulkActionPayload, 
  InventoryProductBulkActionResponse, 
  InventoryProductImagesPayload, 
  InventoryProductListMeta, 
  InventoryProductListQuery, 
  InventoryProductListResponse, 
  InventoryProductUpdatePayload, 
  InventoryProductVariant, 
  InventoryProductVariantValue, 
  InventorySetupOption, 
  InventoryTransferDetail, 
  InventoryTransferFilters, 
  InventoryTransferLine, 
  InventoryTransferStatus, 
  InventoryTransferSummary, 
  InventoryTransferType, 
  UniversalCatalogueProduct, 
  UpdateInventoryProductVariantPayload, 
  ValidateInventoryTransferPayload 
} from '../../apps/inventory/products/products/products.type';




const ON_HAND_OVERRIDES_KEY = 'inventory.on_hand.overrides';
export interface InventoryStockBalance {
  on_hand: string;
  reserved: string;
  available: string;
}

export interface InventoryBalanceCheckResponse {
  location_id: string;
  variants: Array<InventoryStockBalance & { variant_id: string }>;
  items: Array<InventoryStockBalance & { item_id: string; product_id: string; variant_id: string }>;
}
const PRODUCT_TYPE_OPTIONS: InventorySetupOption[] = [
  {
    id: '00000000-0000-0000-0000-000000000101',
    name: 'goods',
    usage_count: 0,
    organisation_id: null,
    is_universal: true,
    source: 'enum',
    created_by: null,
    active: true,
    can_edit: false,
    can_delete: false,
  },
  {
    id: '00000000-0000-0000-0000-000000000102',
    name: 'service',
    usage_count: 0,
    organisation_id: null,
    is_universal: true,
    source: 'enum',
    created_by: null,
    active: true,
    can_edit: false,
    can_delete: false,
  },
  {
    id: '00000000-0000-0000-0000-000000000103',
    name: 'combo',
    usage_count: 0,
    organisation_id: null,
    is_universal: true,
    source: 'enum',
    created_by: null,
    active: true,
    can_edit: false,
    can_delete: false,
  },
];

@Injectable({
  providedIn: 'root',
})
export class InventoryService {
  private readonly backendUrl = environment.backendUrl.replace(/\/$/, '');
  private readonly productDetailRequests = new Map<string, Promise<InventoryProduct | null>>();
  private readonly variantProductRequests = new Map<string, Promise<InventoryProduct | null>>();
  private readonly productItemRequests = new Map<string, Promise<InventoryProductItem[]>>();
  private productItemSession: string | null = null;

  constructor(private readonly restApi: RestApiService, private readonly tokenService: TokenService) {}

  async getProductFormData() {
    return await firstValueFrom(this.restApi.get(INVENTORY_ENDPOINTS.product_form));
  }

  async getProductAttributeOptions(): Promise<InventoryProductAttributeOption[]> {
    const response: unknown = await this.getProductFormData();
    if (!response || typeof response !== 'object') {
      return [];
    }

    const data = response as Record<string, unknown>;
    return this.asProductAttributeOptions(data['attribute_options']);
  }

  async getProductTypes(): Promise<string[]> {
    return PRODUCT_TYPE_OPTIONS.map((option) => option.name);
  }

  async getProductCategories(): Promise<string[]> {
    return await this.getOptionValues(INVENTORY_ENDPOINTS.product_categories, ['product_categories', 'categories']);
  }

  async getUomOptions(): Promise<string[]> {
    return await this.getOptionValues(
      `${INVENTORY_ENDPOINTS.uom_options}?include_inactive=false&source=organisation`,
      ['uom_options', 'uoms'],
    );
  }

  async getTrackingOptions(): Promise<string[]> {
    return await this.getOptionValues(INVENTORY_ENDPOINTS.tracking_options, ['tracking_options']);
  }

  async getProductTypeSetupOptions(): Promise<InventorySetupOption[]> {
    return PRODUCT_TYPE_OPTIONS.map((option) => ({ ...option }));
  }

  async getProductCategorySetupOptions(): Promise<InventorySetupOption[]> {
    return await this.getOptionRecords(INVENTORY_ENDPOINTS.product_categories, ['product_categories', 'categories']);
  }

  async getUomSetupOptions(): Promise<InventorySetupOption[]> {
    return await this.getOptionRecords(INVENTORY_ENDPOINTS.uom_options, ['uom_options', 'uoms']);
  }

  async getTrackingSetupOptions(): Promise<InventorySetupOption[]> {
    return await this.getOptionRecords(INVENTORY_ENDPOINTS.tracking_options, ['tracking_options']);
  }

  createProductType(_payload: { name: string }) {
    throw new Error('Product types are fixed enum values: goods, service, combo.');
  }

  createProductCategory(payload: { name: string }) {
    return this.restApi.post(INVENTORY_ENDPOINTS.product_categories, payload);
  }

  createUomOption(payload: { name: string }) {
    return this.restApi.post(INVENTORY_ENDPOINTS.uom_options, payload);
  }

  createTrackingOption(payload: { name: string }) {
    return this.restApi.post(INVENTORY_ENDPOINTS.tracking_options, payload);
  }

  updateProductTypeOption(_id: string, _payload: { name: string }) {
    throw new Error('Product types are fixed enum values: goods, service, combo.');
  }

  updateProductCategoryOption(id: string, payload: { name: string }) {
    return this.restApi.put(`${INVENTORY_ENDPOINTS.product_categories}/${id}`, payload);
  }

  updateUomOption(id: string, payload: { name: string }) {
    return this.restApi.put(`${INVENTORY_ENDPOINTS.uom_options}/${id}`, payload);
  }

  updateTrackingOption(id: string, payload: { name: string }) {
    return this.restApi.put(`${INVENTORY_ENDPOINTS.tracking_options}/${id}`, payload);
  }

  deleteProductTypeOption(_id: string) {
    throw new Error('Product types are fixed enum values: goods, service, combo.');
  }

  deleteProductCategoryOption(id: string) {
    return this.restApi.delete(`${INVENTORY_ENDPOINTS.product_categories}/${id}`);
  }

  deleteUomOption(id: string) {
    return this.restApi.delete(`${INVENTORY_ENDPOINTS.uom_options}/${id}`);
  }

  deleteTrackingOption(id: string) {
    return this.restApi.delete(`${INVENTORY_ENDPOINTS.tracking_options}/${id}`);
  }



  async runBulkProductAction(
    action: InventoryProductBulkAction,
    productIds: string[],
    payload: InventoryProductBulkActionPayload = {}
  ): Promise<InventoryProductBulkActionResponse> {
    const normalizedIds = productIds
      .map((id) => this.normalizeId(id))
      .filter((id) => id.length > 0);

    const response: unknown = await firstValueFrom(
      this.restApi.post(INVENTORY_ENDPOINTS.products_bulk_actions, {
        action,
        product_ids: normalizedIds,
        payload,
      })
    );

    return this.extractBulkProductActionResponse(response, action, normalizedIds);
  }

  async previewBulkImportProducts(
    rows: InventoryBulkImportProductRowPayload[]
  ): Promise<InventoryBulkImportResponse> {
    return await this.bulkImportProducts(rows, true);
  }

  async importBulkProducts(
    rows: InventoryBulkImportProductRowPayload[]
  ): Promise<InventoryBulkImportResponse> {
    return await this.bulkImportProducts(rows, false);
  }

  async getProducts(options: { status?: 'active' | 'archived' | '' } = { status: 'active' }): Promise<InventoryProduct[]> {
    const endpoint = this.buildProductsEndpoint({
      status: options.status || undefined,
    });
    const response: unknown = await firstValueFrom(this.restApi.get(endpoint));
    return this.applyOnHandOverrides(this.extractProducts(response));
  }

  async getProductsList(query: InventoryProductListQuery = {}): Promise<InventoryProductListResponse> {
    const endpoint = this.buildProductsEndpoint(query);
    const response: unknown = await firstValueFrom(this.restApi.get(endpoint));
    const products = this.applyOnHandOverrides(this.extractProducts(response));
    const meta = this.extractProductListMeta(response, products.length, query);

    return {
      products,
      meta,
    };
  }

  async getManagedProductsList(query: InventoryProductListQuery = {}): Promise<InventoryProductListResponse> {
    const endpoint = this.buildProductsEndpoint(query, INVENTORY_ENDPOINTS.products_management);
    const response: unknown = await firstValueFrom(this.restApi.get(endpoint));
    const products = this.applyOnHandOverrides(this.extractProducts(response));
    return { products, meta: this.extractProductListMeta(response, products.length, query) };
  }

  async getProductById(productId: string): Promise<InventoryProduct | null> {
    const normalizedId = `${productId ?? ''}`.trim();
    if (!normalizedId) {
      return null;
    }

    const cached = this.productDetailRequests.get(normalizedId);
    if (cached) {
      return cached;
    }

    const request = this.fetchProductById(normalizedId).catch((error) => {
      this.productDetailRequests.delete(normalizedId);
      throw error;
    });
    this.productDetailRequests.set(normalizedId, request);
    return request;
  }

  getProductItems(productId: string, locationId?: string | null): Promise<InventoryProductItem[]> {
    const id = String(productId ?? '').trim();
    if (!id) return Promise.resolve([]);
    const location = String(locationId ?? '').trim();
    const session = this.tokenService.getAccessToken();
    if (session !== this.productItemSession) {
      this.productItemRequests.clear();
      this.productItemSession = session;
    }
    const key = `${id}:${location}`;
    const cached = this.productItemRequests.get(key);
    if (cached) return cached;
    // Share concurrent picker preloads, never retain stock-status responses:
    // each subsequent load must recheck canonical Inventory availability.
    const request = firstValueFrom(this.restApi.get<InventoryProductItem[]>(
      INVENTORY_GET_PRODUCT_ITEMS_ENDPOINT.replace('{0}', encodeURIComponent(id))
        + (location ? `?location_id=${encodeURIComponent(location)}` : '')
    )).then((items) => {
      if (session !== this.tokenService.getAccessToken()) throw new Error('The item lookup session changed. Retry.');
      return items;
    }).finally(() => {
      if (this.productItemRequests.get(key) === request) this.productItemRequests.delete(key);
    });
    this.productItemRequests.set(key, request);
    return request;
  }

  async checkBalances(locationId: string, variantIds: string[], itemIds: string[]): Promise<InventoryBalanceCheckResponse> {
    return await firstValueFrom(this.restApi.post<InventoryBalanceCheckResponse>(
      INVENTORY_ENDPOINTS.product_check_balances,
      { location_id: locationId, variant_ids: variantIds, item_ids: itemIds },
    ));
  }

  private async fetchProductById(productId: string): Promise<InventoryProduct | null> {
    const endpoint = INVENTORY_ENDPOINTS.product_by_id.replace('{0}', productId);
    const response: unknown = await firstValueFrom(this.restApi.get(endpoint));
    return this.cacheProductDetail(this.applyOnHandOverride(this.extractProduct(response)));
  }

  async getProductByVariantId(variantId: string): Promise<InventoryProduct | null> {
    const normalizedId = `${variantId ?? ''}`.trim();
    if (!normalizedId) {
      return null;
    }

    const cached = this.variantProductRequests.get(normalizedId);
    if (cached) {
      return cached;
    }

    const request = this.fetchProductByVariantId(normalizedId).catch((error) => {
      this.variantProductRequests.delete(normalizedId);
      throw error;
    });
    this.variantProductRequests.set(normalizedId, request);
    return request;
  }

  private async fetchProductByVariantId(variantId: string): Promise<InventoryProduct | null> {
    const endpoint = INVENTORY_ENDPOINTS.product_by_variant_id.replace('{0}', variantId);
    const response: unknown = await firstValueFrom(this.restApi.get(endpoint));
    return this.cacheProductDetail(this.applyOnHandOverride(this.extractProduct(response)));
  }

  async searchProducts(query: InventoryProductListQuery = {}): Promise<InventoryProductListResponse> {
    return this.getProductsList(query);
  }

  async getSellableProducts(): Promise<InventoryProduct[]> {
    const products = await this.getProducts();
    return products
      .filter((product) => !!product.can_be_sold)
      .sort((left, right) => left.name.localeCompare(right.name));
  }

  async getPurchasableProducts(): Promise<InventoryProduct[]> {
    const products = await this.getProducts();
    return products
      .filter((product) => !!product.can_be_purchased)
      .sort((left, right) => left.name.localeCompare(right.name));
  }



  async archiveProduct(id: string): Promise<InventoryProduct | null> {
    this.clearProductDetailCache(id);
    const endpoint = INVENTORY_ENDPOINTS.product_archive.replace('{0}', id);
    const response: unknown = await firstValueFrom(this.restApi.post(endpoint, {}));
    return this.cacheProductDetail(this.applyOnHandOverride(this.extractProduct(response)));
  }

  async restoreProduct(id: string): Promise<InventoryProduct | null> {
    this.clearProductDetailCache(id);
    const endpoint = INVENTORY_ENDPOINTS.product_restore.replace('{0}', id);
    const response: unknown = await firstValueFrom(this.restApi.post(endpoint, {}));
    return this.cacheProductDetail(this.applyOnHandOverride(this.extractProduct(response)));
  }

  async deleteProduct(id: string): Promise<{ deleted: boolean; message: string }> {
    this.clearProductDetailCache(id);
    const response: unknown = await firstValueFrom(this.restApi.delete(`${INVENTORY_ENDPOINTS.products}/${id}`));

    if (!response || typeof response !== 'object') {
      return { deleted: true, message: 'Product deleted.' };
    }

    const data = response as Record<string, unknown>;
    return {
      deleted: this.asBooleanDefault(data['deleted'], true),
      message: this.asString(data['message']) || 'Product deleted.',
    };
  }

  async updateProductImages(
    id: string,
    payload: InventoryProductImagesPayload
  ): Promise<InventoryProduct | null> {
    this.clearProductDetailCache(id);
    const endpoint = INVENTORY_ENDPOINTS.product_images.replace('{0}', id);
    const response: unknown = await firstValueFrom(
      this.restApi.post(endpoint, {
        main_image: payload.main_image,
        gallery_images: payload.gallery_images,
      })
    );

    return this.cacheProductDetail(this.applyOnHandOverride(this.extractProduct(response)));
  }

  

  async addProductVariant(
    productId: string,
    payload: CreateInventoryProductVariantPayload
  ): Promise<InventoryProduct | null> {
    this.clearProductDetailCache(productId);
    const endpoint = INVENTORY_ENDPOINTS.product_variants.replace('{0}', productId);
    const response: unknown = await firstValueFrom(this.restApi.post(endpoint, payload));
    return this.cacheProductDetail(this.applyOnHandOverride(this.extractProduct(response)));
  }

  async updateProductVariant(
    productId: string,
    variantId: string,
    payload: UpdateInventoryProductVariantPayload
  ): Promise<InventoryProduct | null> {
    this.clearProductDetailCache(productId);
    const endpoint = INVENTORY_ENDPOINTS.product_variant_by_id
      .replace('{0}', productId)
      .replace('{1}', variantId);
    const response: unknown = await firstValueFrom(this.restApi.patch(endpoint, payload));
    return this.cacheProductDetail(this.applyOnHandOverride(this.extractProduct(response)));
  }

  async deleteProductVariant(productId: string, variantId: string): Promise<InventoryProduct | null> {
    this.clearProductDetailCache(productId);
    const endpoint = INVENTORY_ENDPOINTS.product_variant_by_id
      .replace('{0}', productId)
      .replace('{1}', variantId);
    const response: unknown = await firstValueFrom(this.restApi.delete(endpoint));
    return this.cacheProductDetail(this.applyOnHandOverride(this.extractProduct(response)));
  }

  async getTransfers(filters?: InventoryTransferFilters): Promise<InventoryTransferSummary[]> {
    const endpoint = this.buildTransfersEndpoint(filters);
    const response: unknown = await firstValueFrom(this.restApi.get(endpoint));
    return this.extractTransfers(response);
  }

  async getTransferById(id: string): Promise<InventoryTransferDetail | null> {
    const response: unknown = await firstValueFrom(this.restApi.get(`${INVENTORY_ENDPOINTS.transfers}/${id}`));
    return this.extractTransfer(response);
  }

  async createTransfer(payload: CreateInventoryTransferPayload): Promise<InventoryTransferDetail | null> {
    const response: unknown = await firstValueFrom(this.restApi.post(INVENTORY_ENDPOINTS.transfers, payload));
    return this.extractTransfer(response);
  }

  async validateTransfer(
    id: string,
    payload: ValidateInventoryTransferPayload
  ): Promise<InventoryTransferDetail | null> {
    const response: unknown = await firstValueFrom(
      this.restApi.post(`${INVENTORY_ENDPOINTS.transfers}/${id}/validate`, payload)
    );
    return this.extractTransfer(response);
  }

  async cancelTransfer(id: string): Promise<InventoryTransferDetail | null> {
    const response: unknown = await firstValueFrom(this.restApi.post(`${INVENTORY_ENDPOINTS.transfers}/${id}/cancel`, {}));
    return this.extractTransfer(response);
  }

  setProductOnHandQuantity(productId: string, onHandQuantity: number): void {
    const normalizedId = this.normalizeId(productId);
    if (!normalizedId || !Number.isFinite(onHandQuantity)) {
      return;
    }

    const overrides = this.readOnHandOverrides();
    overrides[normalizedId] = onHandQuantity;
    this.writeOnHandOverrides(overrides);
  }

  clearProductOnHandQuantityOverride(productId: string): void {
    const normalizedId = this.normalizeId(productId);
    if (!normalizedId) {
      return;
    }

    const overrides = this.readOnHandOverrides();
    if (!(normalizedId in overrides)) {
      return;
    }

    delete overrides[normalizedId];
    this.writeOnHandOverrides(overrides);
  }

  resolveProductImageUrl(value: string | null): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    if (
      trimmed.startsWith('http://') ||
      trimmed.startsWith('https://') ||
      trimmed.startsWith('data:') ||
      trimmed.startsWith('blob:')
    ) {
      return trimmed;
    }

    if (trimmed.startsWith('/')) {
      return `${this.backendUrl}${trimmed}`;
    }

    return `${this.backendUrl}/${trimmed}`;
  }

  private async bulkImportProducts(
    rows: InventoryBulkImportProductRowPayload[],
    dryRun: boolean
  ): Promise<InventoryBulkImportResponse> {
    const response: unknown = await firstValueFrom(
      this.restApi.post(INVENTORY_ENDPOINTS.products_bulk_import, {
        dry_run: dryRun,
        rows,
      })
    );

    return this.extractBulkImportResponse(response, dryRun, rows.length);
  }

  private async getOptionValues(endpoint: string, responseKeys: string[]): Promise<string[]> {
    const response: unknown = await firstValueFrom(this.restApi.get(endpoint));
    return this.extractOptionValues(response, responseKeys);
  }

  private async getOptionRecords(endpoint: string, responseKeys: string[]): Promise<InventorySetupOption[]> {
    const response: unknown = await firstValueFrom(this.restApi.get(endpoint));
    return this.extractOptionRecords(response, responseKeys);
  }

  private buildProductsEndpoint(
    query?: InventoryProductListQuery,
    baseEndpoint = INVENTORY_ENDPOINTS.products
  ): string {
    if (!query) {
      return baseEndpoint;
    }

    const params = new URLSearchParams();

    if (typeof query.page === 'number' && Number.isFinite(query.page) && query.page > 0) {
      params.set('page', `${Math.floor(query.page)}`);
    }

    if (typeof query.per_page === 'number' && Number.isFinite(query.per_page) && query.per_page > 0) {
      params.set('per_page', `${Math.floor(query.per_page)}`);
    }

    if (typeof query.paginated === 'boolean') {
      params.set('paginated', query.paginated ? 'true' : 'false');
    }

    if (typeof query.search === 'string' && query.search.trim()) {
      params.set('search', query.search.trim());
    }

    if (typeof query.search_field === 'string' && query.search_field.trim()) {
      params.set('search_field', query.search_field.trim());
    }

    if (typeof query.can_be_sold === 'boolean') {
      params.set('can_be_sold', query.can_be_sold ? 'true' : 'false');
    }

    if (typeof query.can_be_purchased === 'boolean') {
      params.set('can_be_purchased', query.can_be_purchased ? 'true' : 'false');
    }

    if (typeof query.available_in_pos === 'boolean') {
      params.set('available_in_pos', query.available_in_pos ? 'true' : 'false');
    }

    if (typeof query.is_subscription_product === 'boolean') {
      params.set('is_subscription_product', query.is_subscription_product ? 'true' : 'false');
    }

    if (typeof query.is_rental_product === 'boolean') {
      params.set('is_rental_product', query.is_rental_product ? 'true' : 'false');
    }

    if (typeof query.tracking === 'string' && query.tracking.trim()) {
      params.set('tracking', query.tracking.trim());
    }

    if (typeof query.product_type === 'string' && query.product_type.trim()) {
      params.set('product_type', query.product_type.trim());
    }

    if (typeof query.category === 'string' && query.category.trim()) {
      params.set('category', query.category.trim());
    }

    if (typeof query.uom === 'string' && query.uom.trim()) {
      params.set('uom', query.uom.trim());
    }

    if (typeof query.status === 'string' && query.status.trim()) {
      params.set('status', query.status.trim());
    }

    if (typeof query.min_price === 'number' && Number.isFinite(query.min_price)) {
      params.set('min_price', `${query.min_price}`);
    }

    if (typeof query.max_price === 'number' && Number.isFinite(query.max_price)) {
      params.set('max_price', `${query.max_price}`);
    }

    if (typeof query.min_stock_qty === 'number' && Number.isFinite(query.min_stock_qty)) {
      params.set('min_stock_qty', `${Math.floor(query.min_stock_qty)}`);
    }

    if (typeof query.max_stock_qty === 'number' && Number.isFinite(query.max_stock_qty)) {
      params.set('max_stock_qty', `${Math.floor(query.max_stock_qty)}`);
    }

    if (typeof query.sort_by === 'string' && query.sort_by.trim()) {
      params.set('sort_by', query.sort_by.trim());
    }

    if (typeof query.sort_order === 'string' && query.sort_order.trim()) {
      params.set('sort_order', query.sort_order.trim());
    }

    const queryString = params.toString();
    if (!queryString) {
      return baseEndpoint;
    }

    return `${baseEndpoint}?${queryString}`;
  }

  private clearProductDetailCache(productId: string): void {
    const normalizedId = `${productId ?? ''}`.trim();
    if (normalizedId) {
      this.productDetailRequests.delete(normalizedId);
    }
    this.variantProductRequests.clear();
  }

  private cacheProductDetail(product: InventoryProduct | null): InventoryProduct | null {
    if (product?.id) {
      this.productDetailRequests.set(product.id, Promise.resolve(product));
    }

    return product;
  }

  private buildTransfersEndpoint(filters?: InventoryTransferFilters): string {
    if (!filters) {
      return INVENTORY_ENDPOINTS.transfers;
    }

    const query = new URLSearchParams();

    if (filters.transfer_type) {
      query.set('transfer_type', filters.transfer_type);
    }

    if (filters.status) {
      query.set('status', filters.status);
    }

    const queryString = query.toString();
    if (!queryString) {
      return INVENTORY_ENDPOINTS.transfers;
    }

    return `${INVENTORY_ENDPOINTS.transfers}?${queryString}`;
  }

  private extractOptionValues(response: unknown, responseKeys: string[]): string[] {
    if (Array.isArray(response)) {
      return this.normalizeOptionValues(response);
    }

    if (!response || typeof response !== 'object') {
      return [];
    }

    const data = response as Record<string, unknown>;

    for (const key of responseKeys) {
      const value = data[key];
      if (Array.isArray(value)) {
        return this.normalizeOptionValues(value);
      }
    }

    for (const fallbackKey of ['results', 'items', 'data']) {
      const value = data[fallbackKey];
      if (Array.isArray(value)) {
        return this.normalizeOptionValues(value);
      }
    }

    return [];
  }

  private extractOptionRecords(response: unknown, responseKeys: string[]): InventorySetupOption[] {
    if (Array.isArray(response)) {
      return this.normalizeOptionRecords(response);
    }

    if (!response || typeof response !== 'object') {
      return [];
    }

    const data = response as Record<string, unknown>;

    for (const key of responseKeys) {
      const value = data[key];
      if (Array.isArray(value)) {
        return this.normalizeOptionRecords(value);
      }
    }

    for (const fallbackKey of ['results', 'items', 'data']) {
      const value = data[fallbackKey];
      if (Array.isArray(value)) {
        return this.normalizeOptionRecords(value);
      }
    }

    return [];
  }

  private normalizeOptionValues(values: unknown[]): string[] {
    return values
      .map((value) => {
        if (typeof value === 'string') {
          return value.trim();
        }

        if (!value || typeof value !== 'object') {
          return '';
        }

        const valueObject = value as Record<string, unknown>;
        const optionLabel = valueObject['name'] ?? valueObject['label'] ?? valueObject['value'];

        return typeof optionLabel === 'string' ? optionLabel.trim() : '';
      })
      .filter((value) => value.length > 0);
  }

  private normalizeOptionRecords(values: unknown[]): InventorySetupOption[] {
    return values
      .map((value) => this.normalizeOptionRecord(value))
      .filter((value): value is InventorySetupOption => value !== null);
  }

  private normalizeOptionRecord(value: unknown): InventorySetupOption | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const data = value as Record<string, unknown>;
    const id = data['id'];
    const name = data['name'];

    if ((typeof id !== 'string' && typeof id !== 'number') || typeof name !== 'string') {
      return null;
    }

    return {
      id: String(id),
      name: name.trim(),
      usage_count: this.asPositiveInteger(data['usage_count'], 0),
      organisation_id: this.asNullableString(data['organisation_id']),
      is_universal: this.asBoolean(data['is_universal']),
      source:
        this.asString(data['source']) ||
        (this.asBoolean(data['is_universal']) ? 'universal' : 'organisation'),
      created_by: this.asNullableString(data['created_by']),
      active:
        typeof data['active'] === 'boolean'
          ? data['active']
          : true,
      can_edit:
        typeof data['can_edit'] === 'boolean'
          ? data['can_edit']
          : true,
      can_delete: typeof data['can_delete'] === 'boolean' ? data['can_delete'] : true,
    };
  }

  private extractProducts(response: unknown): InventoryProduct[] {
    if (Array.isArray(response)) {
      return this.normalizeProducts(response);
    }

    if (!response || typeof response !== 'object') {
      return [];
    }

    const data = response as Record<string, unknown>;

    for (const key of ['products', 'results', 'items', 'data']) {
      const value = data[key];
      if (Array.isArray(value)) {
        return this.normalizeProducts(value);
      }

      if (key === 'data' && value && typeof value === 'object') {
        const nestedProducts = this.extractProducts(value);
        if (nestedProducts.length > 0) {
          return nestedProducts;
        }
      }
    }

    return [];
  }

  private extractProductListMeta(
    response: unknown,
    fallbackTotal: number,
    query: InventoryProductListQuery
  ): InventoryProductListMeta {
    const fallbackPage = this.asPositiveInteger(query.page, 1);
    const fallbackPerPage = this.asPositiveInteger(
      query.per_page,
      fallbackTotal > 0 ? fallbackTotal : 20
    );

    if (!response || typeof response !== 'object') {
      const totalPages = fallbackPerPage > 0
        ? Math.ceil(fallbackTotal / fallbackPerPage)
        : 0;

      return {
        page: fallbackPage,
        per_page: fallbackPerPage,
        total: fallbackTotal,
        total_pages: totalPages,
        has_next: totalPages > 0 && fallbackPage < totalPages,
        has_prev: fallbackPage > 1,
      };
    }

    const rawData = response as Record<string, unknown>;
    const nestedData =
      rawData['data'] && typeof rawData['data'] === 'object'
        ? (rawData['data'] as Record<string, unknown>)
        : null;
    const data = nestedData ?? rawData;
    const page = this.asPositiveInteger(data['page'], fallbackPage);
    const perPage = this.asPositiveInteger(data['per_page'], fallbackPerPage);
    const total = this.asPositiveInteger(data['total'], fallbackTotal);
    const derivedTotalPages = perPage > 0 ? Math.ceil(total / perPage) : 0;
    const totalPages = this.asPositiveInteger(data['total_pages'], derivedTotalPages);

    return {
      page,
      per_page: perPage,
      total,
      total_pages: totalPages,
      has_next:
        typeof data['has_next'] === 'boolean'
          ? data['has_next']
          : totalPages > 0 && page < totalPages,
      has_prev:
        typeof data['has_prev'] === 'boolean'
          ? data['has_prev']
      : page > 1,
    };
  }

  private extractBulkProductActionResponse(
    response: unknown,
    fallbackAction: string,
    requestedIds: string[]
  ): InventoryProductBulkActionResponse {
    const fallbackResults = requestedIds.map((id) => ({
      product_id: id,
      success: false,
      message: 'No response received for this product action.',
    }));

    if (!response || typeof response !== 'object') {
      return {
        action: fallbackAction,
        total_requested: requestedIds.length,
        success_count: 0,
        failure_count: requestedIds.length,
        results: fallbackResults,
      };
    }

    const data = response as Record<string, unknown>;
    const results = this.normalizeBulkProductActionResults(data['results'], fallbackResults);
    const successCount = this.asPositiveInteger(
      data['success_count'],
      results.filter((item) => item.success).length
    );
    const failureCount = this.asPositiveInteger(
      data['failure_count'],
      results.filter((item) => !item.success).length
    );

    return {
      action: this.asString(data['action']) || fallbackAction,
      total_requested: this.asPositiveInteger(data['total_requested'], results.length),
      success_count: successCount,
      failure_count: failureCount,
      results,
    };
  }

  private normalizeBulkProductActionResults(
    value: unknown,
    fallbackResults: InventoryProductBulkActionItemResult[]
  ): InventoryProductBulkActionItemResult[] {
    if (!Array.isArray(value)) {
      return fallbackResults;
    }

    const normalized = value
      .map((item) => this.normalizeBulkProductActionResult(item))
      .filter((item): item is InventoryProductBulkActionItemResult => item !== null);

    return normalized.length > 0 ? normalized : fallbackResults;
  }

  private normalizeBulkProductActionResult(value: unknown): InventoryProductBulkActionItemResult | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const data = value as Record<string, unknown>;
    const productId = this.asString(data['product_id']);

    if (!productId) {
      return null;
    }

    return {
      product_id: productId,
      success: this.asBoolean(data['success']),
      message: this.asString(data['message']) || 'No message provided.',
    };
  }

  private extractBulkImportResponse(
    response: unknown,
    fallbackDryRun: boolean,
    fallbackRows: number
  ): InventoryBulkImportResponse {
    if (!response || typeof response !== 'object') {
      return {
        dry_run: fallbackDryRun,
        total_rows: fallbackRows,
        valid_rows: 0,
        invalid_rows: fallbackRows,
        created_rows: 0,
        duplicate_rows: 0,
        results: [],
      };
    }

    const data = response as Record<string, unknown>;
    const results = this.normalizeBulkImportResults(data['results']);

    return {
      dry_run: typeof data['dry_run'] === 'boolean' ? data['dry_run'] : fallbackDryRun,
      total_rows: this.asPositiveInteger(data['total_rows'], fallbackRows),
      valid_rows: this.asPositiveInteger(data['valid_rows'], results.filter((item) => item.success).length),
      invalid_rows: this.asPositiveInteger(data['invalid_rows'], results.filter((item) => !item.success).length),
      created_rows: this.asPositiveInteger(data['created_rows'], 0),
      duplicate_rows: this.asPositiveInteger(data['duplicate_rows'], 0),
      results,
    };
  }

  private normalizeBulkImportResults(value: unknown): InventoryBulkImportRowResult[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item) => this.normalizeBulkImportResult(item))
      .filter((item): item is InventoryBulkImportRowResult => item !== null);
  }

  private normalizeBulkImportResult(value: unknown): InventoryBulkImportRowResult | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const data = value as Record<string, unknown>;
    const rowNumber = this.asPositiveInteger(data['row_number'], 0);

    if (rowNumber <= 0) {
      return null;
    }

    return {
      row_number: rowNumber,
      success: this.asBoolean(data['success']),
      errors: Array.isArray(data['errors'])
        ? data['errors'].filter((item): item is string => typeof item === 'string')
        : [],
      product_id: this.asNullableString(data['product_id']),
    };
  }

  private normalizeProducts(values: unknown[]): InventoryProduct[] {
    return values
      .map((value) => this.normalizeProduct(value))
      .filter((value): value is InventoryProduct => value !== null);
  }

  private extractProduct(response: unknown): InventoryProduct | null {
    const direct = this.normalizeProduct(response);
    if (direct) {
      return direct;
    }

    if (!response || typeof response !== 'object') {
      return null;
    }

    const data = response as Record<string, unknown>;

    for (const key of ['product', 'item', 'data']) {
      const value = data[key];
      const product = this.normalizeProduct(value);
      if (product) {
        return product;
      }
    }

    return null;
  }

  private normalizeProduct(value: unknown): InventoryProduct | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const data = value as Record<string, unknown>;
    const id = data['id'];
    const name = data['name'];

    if ((typeof id !== 'string' && typeof id !== 'number') || typeof name !== 'string') {
      return null;
    }

    return {
      id: String(id),
      status: data['status'] === 'draft' || data['status'] === 'archived' ? data['status'] : 'active',
      name,
      product_type: this.asString(data['product_type']),
      invoicing_policy: this.asNullableString(data['invoicing_policy']),
      sales_tax_ids: this.asStringArray(data['sales_tax_ids']),
      purchase_tax_ids: this.asStringArray(data['purchase_tax_ids']),
      income_account_id: this.asNullableString(data['income_account_id']),
      expense_account_id: this.asNullableString(data['expense_account_id']),
      category: this.asString(data['category']),
      category_id: this.asNullableString(data['category_id']),
      storage_category_id: this.asNullableString(data['storage_category_id']),
      universal_catalogue_product_id: this.asNullableString(data['universal_catalogue_product_id']),
      internal_reference: this.asNullableString(data['internal_reference']),
      barcode: this.asNullableString(data['barcode']),
      description: this.asNullableString(data['description']),
      on_hand_quantity: this.asNumber(data['on_hand_quantity']),
      uom: this.asString(data['uom']),
      uom_id: this.asNullableString(data['uom_id']),
      purchase_uom_id: this.asNullableString(data['purchase_uom_id']),
      inventory_valuation_account_id: this.asNullableString(data['inventory_valuation_account_id']),
      stock_input_account_id: this.asNullableString(data['stock_input_account_id']),
      stock_output_account_id: this.asNullableString(data['stock_output_account_id']),
      costing_method: this.asString(data['costing_method']) || 'fifo',
      inventory_valuation: this.asString(data['inventory_valuation']) || 'automated',
      tracking: this.asString(data['tracking']),
      sales_price: this.asStringOrNumber(data['sales_price']),
      cost: this.asStringOrNumber(data['cost']),
      can_be_sold: this.asBoolean(data['can_be_sold']),
      can_be_purchased: this.asBoolean(data['can_be_purchased']),
      available_in_pos: this.asBoolean(data['available_in_pos']),
      pos_product_category_id: this.asNullableString(data['pos_product_category_id']),
      pos_sequence: this.asNumber(data['pos_sequence']),
      pos_color: this.asNullableString(data['pos_color']),
      is_weighable: this.asBoolean(data['is_weighable']),
      combo_choice_ids: this.asStringArray(data['combo_choice_ids']),
      is_subscription_product: this.asBoolean(data['is_subscription_product']),
      is_rental_product: this.asBoolean(data['is_rental_product']),
      is_fixed_asset: this.asBoolean(data['is_fixed_asset']),
      unique_item_attribute_keys: (Array.isArray(data['unique_item_attribute_keys']) ? data['unique_item_attribute_keys'] : [])
        .map((item: unknown) => typeof item === 'string'
          ? { name: item.trim(), required: true }
          : { name: String((item as Record<string, unknown>)?.['name'] ?? '').trim(), required: (item as Record<string, unknown>)?.['required'] !== false })
        .filter((item: { name: string }) => !!item.name),
      fixed_asset_defaults: data['fixed_asset_defaults'] && typeof data['fixed_asset_defaults'] === 'object' && !Array.isArray(data['fixed_asset_defaults'])
        ? data['fixed_asset_defaults'] as Record<string, unknown> : {},
      rental_pricing: data['rental_pricing'] && typeof data['rental_pricing'] === 'object' && !Array.isArray(data['rental_pricing'])
        ? data['rental_pricing'] as Record<string, unknown> : {},
      subscription_pricing: data['subscription_pricing'] && typeof data['subscription_pricing'] === 'object' && !Array.isArray(data['subscription_pricing'])
        ? data['subscription_pricing'] as Record<string, unknown> : {},
      main_image: this.asNullableString(data['main_image']),
      gallery_images: this.asStringArray(data['gallery_images']),
      attribute_lines: this.asProductAttributeLines(data['attribute_lines']),
      variants: this.asProductVariantArray(data['variants']),
      created_at: this.asNullableString(data['created_at']),
      updated_at: this.asNullableString(data['updated_at']),
    };
  }

  private extractUniversalCatalogueProducts(
    response: unknown,
    keys: string[] = ['products', 'suggestions', 'items', 'data']
  ): UniversalCatalogueProduct[] {
    if (Array.isArray(response)) {
      return this.normalizeUniversalCatalogueProducts(response);
    }

    if (!response || typeof response !== 'object') {
      return [];
    }

    const data = response as Record<string, unknown>;
    for (const key of keys) {
      const value = data[key];
      if (Array.isArray(value)) {
        return this.normalizeUniversalCatalogueProducts(value);
      }
    }

    return [];
  }

  private extractUniversalCatalogueProduct(response: unknown): UniversalCatalogueProduct | null {
    const direct = this.normalizeUniversalCatalogueProduct(response);
    if (direct) {
      return direct;
    }

    if (!response || typeof response !== 'object') {
      return null;
    }

    const data = response as Record<string, unknown>;
    for (const key of ['product', 'item', 'data']) {
      const value = data[key];
      const normalized = this.normalizeUniversalCatalogueProduct(value);
      if (normalized) {
        return normalized;
      }
    }

    return null;
  }

  private normalizeUniversalCatalogueProducts(values: unknown[]): UniversalCatalogueProduct[] {
    return values
      .map((value) => this.normalizeUniversalCatalogueProduct(value))
      .filter((value): value is UniversalCatalogueProduct => value !== null);
  }

  private normalizeUniversalCatalogueProduct(value: unknown): UniversalCatalogueProduct | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const data = value as Record<string, unknown>;
    const id = data['id'];
    const name = data['name'];

    if ((typeof id !== 'string' && typeof id !== 'number') || typeof name !== 'string') {
      return null;
    }

    return {
      id: String(id),
      name,
      product_type: this.asString(data['product_type']),
      category: this.asString(data['category']),
      internal_reference: this.asNullableString(data['internal_reference']),
      barcode: this.asNullableString(data['barcode']),
      description: this.asNullableString(data['description']),
      uom: this.asString(data['uom']),
      tracking: this.asString(data['tracking']),
      suggested_sales_price: this.asStringOrNumber(data['suggested_sales_price']),
      suggested_cost: this.asStringOrNumber(data['suggested_cost']),
      can_be_sold: this.asBoolean(data['can_be_sold']),
      can_be_purchased: this.asBoolean(data['can_be_purchased']),
      brand: this.asNullableString(data['brand']),
      default_image: this.asNullableString(data['default_image']),
      is_archived: this.asBoolean(data['is_archived']),
    };
  }

  private extractTransfers(response: unknown): InventoryTransferSummary[] {
    if (Array.isArray(response)) {
      return this.normalizeTransfers(response);
    }

    if (!response || typeof response !== 'object') {
      return [];
    }

    const data = response as Record<string, unknown>;

    for (const key of ['transfers', 'results', 'items', 'data']) {
      const value = data[key];
      if (Array.isArray(value)) {
        return this.normalizeTransfers(value);
      }
    }

    return [];
  }

  private normalizeTransfers(values: unknown[]): InventoryTransferSummary[] {
    return values
      .map((value) => this.normalizeTransferSummary(value))
      .filter((value): value is InventoryTransferSummary => value !== null);
  }

  private extractTransfer(response: unknown): InventoryTransferDetail | null {
    const direct = this.normalizeTransferDetail(response);
    if (direct) {
      return direct;
    }

    if (!response || typeof response !== 'object') {
      return null;
    }

    const data = response as Record<string, unknown>;

    for (const key of ['transfer', 'item', 'data']) {
      const value = data[key];
      const transfer = this.normalizeTransferDetail(value);
      if (transfer) {
        return transfer;
      }
    }

    return null;
  }

  private normalizeTransferSummary(value: unknown): InventoryTransferSummary | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const data = value as Record<string, unknown>;
    const id = data['id'];
    const reference = data['reference'];

    if ((typeof id !== 'string' && typeof id !== 'number') || typeof reference !== 'string') {
      return null;
    }

    return {
      id: String(id),
      organisation_id: this.asNullableString(data['organisation_id']),
      reference,
      transfer_type: this.asTransferType(data['transfer_type']),
      status: this.asTransferStatus(data['status']),
      source_location: this.asNullableString(data['source_location']),
      source_site_id: this.asNullableString(data['source_site_id']),
      source_location_id: this.asNullableString(data['source_location_id']),
      source_bin_id: this.asNullableString(data['source_bin_id']),
      destination_location: this.asNullableString(data['destination_location']),
      destination_site_id: this.asNullableString(data['destination_site_id']),
      destination_location_id: this.asNullableString(data['destination_location_id']),
      destination_bin_id: this.asNullableString(data['destination_bin_id']),
      scheduled_date: this.asNullableString(data['scheduled_date']),
      completed_at: this.asNullableString(data['completed_at']),
      notes: this.asNullableString(data['notes']),
      created_by: this.asNullableString(data['created_by']),
      created_at: this.asNullableString(data['created_at']),
      updated_at: this.asNullableString(data['updated_at']),
    };
  }

  private normalizeTransferDetail(value: unknown): InventoryTransferDetail | null {
    const summary = this.normalizeTransferSummary(value);

    if (!summary) {
      return null;
    }

    const data = value as Record<string, unknown>;

    return {
      ...summary,
      lines: this.normalizeTransferLines(data['lines']),
    };
  }

  private normalizeTransferLines(value: unknown): InventoryTransferLine[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((line) => this.normalizeTransferLine(line))
      .filter((line): line is InventoryTransferLine => line !== null);
  }

  private normalizeTransferLine(value: unknown): InventoryTransferLine | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const data = value as Record<string, unknown>;
    const id = data['id'];

    if (typeof id !== 'string' && typeof id !== 'number') {
      return null;
    }

    return {
      id: String(id),
      transfer_id: this.asString(data['transfer_id']),
      product_id: this.asString(data['product_id']),
      product_name: this.asString(data['product_name']),
      lot_serial: this.asNullableString(data['lot_serial']),
      requested_qty: this.asNumber(data['requested_qty']),
      done_qty: this.asNumber(data['done_qty']),
    };
  }

  private asTransferType(value: unknown): InventoryTransferType {
    const normalized = typeof value === 'string' ? value.toLowerCase() : '';

    if (normalized === 'incoming' || normalized === 'outgoing' || normalized === 'internal') {
      return normalized;
    }

    return 'internal';
  }

  private asTransferStatus(value: unknown): InventoryTransferStatus {
    const normalized = typeof value === 'string' ? value.toLowerCase() : '';

    if (normalized === 'draft' || normalized === 'waiting' || normalized === 'done' || normalized === 'cancelled') {
      return normalized;
    }

    return 'draft';
  }

  private asString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private asNullableString(value: unknown): string | null {
    return typeof value === 'string' ? value : null;
  }

  private asStringOrNumber(value: unknown): string | number {
    if (typeof value === 'number' || typeof value === 'string') {
      return value;
    }

    return '';
  }

  private asNumber(value: unknown): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    return 0;
  }

  private asPositiveInteger(value: unknown, fallback: number): number {
    const parsed = this.asNumber(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return Math.max(0, Math.floor(fallback));
    }

    return Math.floor(parsed);
  }

  private asBoolean(value: unknown): boolean {
    return typeof value === 'boolean' ? value : false;
  }

  private asBooleanDefault(value: unknown, fallback: boolean): boolean {
    return typeof value === 'boolean' ? value : fallback;
  }

  private asStringArray(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.filter((item): item is string => typeof item === 'string');
  }

  private asProductVariantArray(value: unknown): InventoryProductVariant[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item) => this.asProductVariant(item))
      .filter((item): item is InventoryProductVariant => item !== null);
  }

  private asProductAttributeOptions(value: unknown): InventoryProductAttributeOption[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item) => this.asProductAttributeOption(item))
      .filter((item): item is InventoryProductAttributeOption => item !== null);
  }

  private asProductAttributeOption(value: unknown): InventoryProductAttributeOption | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const data = value as Record<string, unknown>;
    const id = this.asString(data['id']).trim();
    const name = this.asString(data['name']).trim();
    if (!id || !name) {
      return null;
    }

    return {
      id,
      name,
      active: this.asBooleanDefault(data['active'], true),
      display_type: this.asNullableString(data['display_type']),
      variant_creation_mode: this.asNullableString(data['variant_creation_mode']),
      values: this.asProductAttributeValueOptions(data['values']),
    };
  }

  private asProductAttributeValueOptions(value: unknown): InventoryProductAttributeValueOption[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item) => this.asProductAttributeValueOption(item))
      .filter((item): item is InventoryProductAttributeValueOption => item !== null);
  }

  private asProductAttributeValueOption(value: unknown): InventoryProductAttributeValueOption | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const data = value as Record<string, unknown>;
    const id = this.asString(data['id']).trim();
    const attributeId = this.asString(data['attribute_id']).trim();
    const label = this.asString(data['value']).trim();
    if (!id || !attributeId || !label) {
      return null;
    }

    return {
      id,
      attribute_id: attributeId,
      value: label,
      active: this.asBooleanDefault(data['active'], true),
      price_extra: this.asNullableString(data['price_extra']) ?? (typeof data['price_extra'] === 'number' ? data['price_extra'] : null),
    };
  }

  private asProductAttributeLines(value: unknown): InventoryProductAttributeLine[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item) => this.asProductAttributeLine(item))
      .filter((item): item is InventoryProductAttributeLine => item !== null);
  }

  private asProductAttributeLine(value: unknown): InventoryProductAttributeLine | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const data = value as Record<string, unknown>;
    const attributeId = this.asString(data['attribute_id']).trim();
    if (!attributeId) {
      return null;
    }

    return {
      id: this.asString(data['id']).trim(),
      attribute_id: attributeId,
      attribute_name: this.asString(data['attribute_name']).trim(),
      display_type: this.asNullableString(data['display_type']),
      selected_value_ids: this.asStringArray(data['selected_value_ids']),
      selected_values: this.asProductAttributeValueOptions(data['selected_values']),
    };
  }

  private asProductVariantValues(value: unknown): InventoryProductVariantValue[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item) => this.asProductVariantValue(item))
      .filter((item): item is InventoryProductVariantValue => item !== null);
  }

  private asProductVariantValue(value: unknown): InventoryProductVariantValue | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const data = value as Record<string, unknown>;
    const attributeValueId = this.asString(data['attribute_value_id']).trim();
    if (!attributeValueId) {
      return null;
    }

    return {
      attribute_id: this.asString(data['attribute_id']).trim(),
      attribute_name: this.asString(data['attribute_name']).trim(),
      attribute_value_id: attributeValueId,
      value: this.asString(data['value']).trim(),
    };
  }

  private asProductVariant(value: unknown): InventoryProductVariant | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const data = value as Record<string, unknown>;
    const id = this.asString(data['id']).trim();
    const name = this.asString(data['name']).trim();
    if (!id || !name) {
      return null;
    }

    return {
      id,
      name,
      sku: this.asNullableString(data['sku']),
      code: this.asNullableString(data['code']),
      barcode: this.asNullableString(data['barcode']),
      attribute_value_ids: this.asStringArray(data['attribute_value_ids']),
      values: this.asProductVariantValues(data['values']),
      size: this.asNullableString(data['size']),
      color: this.asNullableString(data['color']),
      material: this.asNullableString(data['material']),
      packaging: this.asNullableString(data['packaging']),
      price: this.asStringOrNumber(data['price']),
      cost: this.asStringOrNumber(data['cost']),
      price_adjustment: this.asStringOrNumber(data['price_adjustment'] ?? 0),
      cost_extra: this.asStringOrNumber(data['cost_extra'] ?? 0),
      on_hand_quantity: this.asNumber(data['on_hand_quantity']),
      main_image: this.asNullableString(data['main_image']),
      gallery_images: this.asStringArray(data['gallery_images']),
      available_for_sale: this.asBooleanDefault(data['available_for_sale'], true),
      available_for_purchase: this.asBooleanDefault(data['available_for_purchase'], false),
      weight: this.asNullableString(data['weight']) ?? (typeof data['weight'] === 'number' ? data['weight'] : null),
      volume: this.asNullableString(data['volume']) ?? (typeof data['volume'] === 'number' ? data['volume'] : null),
      active: this.asBooleanDefault(data['active'], true),
      disabled_at: this.asNullableString(data['disabled_at']),
      archived_at: this.asNullableString(data['archived_at']),
      created_at: this.asNullableString(data['created_at']),
      updated_at: this.asNullableString(data['updated_at']),
    };
  }

  private applyOnHandOverrides(products: InventoryProduct[]): InventoryProduct[] {
    const overrides = this.readOnHandOverrides();

    return products.map((product) => this.applyOnHandOverride(product, overrides)).filter((product): product is InventoryProduct => product !== null);
  }

  private applyOnHandOverride(
    product: InventoryProduct | null,
    overrides: Record<string, number> = this.readOnHandOverrides()
  ): InventoryProduct | null {
    if (!product) {
      return null;
    }

    const normalizedId = this.normalizeId(product.id);
    if (!normalizedId || !(normalizedId in overrides)) {
      return product;
    }

    return {
      ...product,
      on_hand_quantity: overrides[normalizedId],
    };
  }

  private readOnHandOverrides(): Record<string, number> {
    try {
      const raw = localStorage.getItem(ON_HAND_OVERRIDES_KEY);
      if (!raw) {
        return {};
      }

      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') {
        return {};
      }

      const normalized: Record<string, number> = {};

      for (const [rawId, rawQuantity] of Object.entries(parsed as Record<string, unknown>)) {
        const id = this.normalizeId(rawId);
        const quantity = this.asNumber(rawQuantity);

        if (!id || !Number.isFinite(quantity)) {
          continue;
        }

        normalized[id] = quantity;
      }

      return normalized;
    } catch {
      return {};
    }
  }

  private writeOnHandOverrides(overrides: Record<string, number>): void {
    try {
      localStorage.setItem(ON_HAND_OVERRIDES_KEY, JSON.stringify(overrides));
    } catch {
      // Ignore local storage write failures.
    }
  }

  private normalizeId(value: string | null | undefined): string {
    if (typeof value !== 'string') {
      return '';
    }

    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : '';
  }
}
